import React, { useMemo, useState } from "react";
import Dropdown from "../../common/dropDown";
import IsfSiteTemplateFields from "./IsfSiteTemplateFields";
import IsfReferenceDatePicker from "./IsfReferenceDatePicker";
import IsfReferenceDateHint from "./IsfReferenceDateHint";
import { isActiveStudy } from "../../../services/lookupService";
import { resolveIsfOpenBinderCategories } from "../../../utils/sponsorCroAccess";
import "./isfStudyPicker.scss";

function RequiredLabel({ children }) {
  return (
    <label className="form-label">
      {children} <span className="text-danger">*</span>
    </label>
  );
}

function studyHasBinder(study) {
  return !!(study?.HaseISFTemplate || study?.HasePSFTemplate);
}

function formatStudyListLabel(study) {
  const code = String(study?.StudyNumber || "").trim();
  const name = String(study?.StudyName || "").trim() || "Untitled study";
  return { code, name };
}

function studyColor(study) {
  return study?.StudySchColor || study?.StudyColor || "#1e3a5f";
}

function BinderBadge({ label, active, kind = "eisf" }) {
  if (!active) return null;
  return (
    <span className={`isf-study-picker__badge isf-study-picker__badge--${kind}`}>
      {label}
    </span>
  );
}

function StudyLifecycleBadge({ study }) {
  const isLive = isActiveStudy(study);
  return (
    <span
      className={`isf-study-picker__lifecycle ${
        isLive
          ? "isf-study-picker__lifecycle--live"
          : "isf-study-picker__lifecycle--archived"
      }`}
    >
      {isLive ? "Live" : "Archived"}
    </span>
  );
}

export default function IsfStudyPickerModal({
  open,
  onClose,
  studies = [],
  studiesLoading = false,
  selectedStudy,
  onSelectStudy,
  formData,
  templateCategoryOptions = [],
  templateCategoryCreationOptions = [],
  templateOptions = [],
  createModalSites = [],
  createModalSitesLoading = false,
  isMultiSiteCreateModal = false,
  sameTemplateForAllSites = true,
  onSameTemplateForAllChange,
  perSiteTemplateOptions = {},
  onPerSiteTemplateChange,
  createReferenceDate = "",
  onCreateReferenceDateChange,
  perSiteReferenceDates = {},
  onPerSiteReferenceDateChange,
  templateSetupInfo = "",
  templatesLoading = false,
  showCreatePanel = false,
  onSelectTemplateCategory,
  onSelectTemplateCategoryCreation,
  onSelectTemplate,
  onOpenStudy,
  onCreateStudy,
  onRequestCreate,
  emptyListMessage = "",
  /** When false (Sponsor/CRO), hide all create-binder actions. */
  allowCreateBinder = true,
  userData = null,
}) {
  const [search, setSearch] = useState("");

  const selectedRecord = useMemo(() => {
    if (!selectedStudy?.value) return null;
    return (
      studies.find(
        (row) =>
          String(row.StudyId).toLowerCase() ===
          String(selectedStudy.value).toLowerCase(),
      ) || null
    );
  }, [studies, selectedStudy]);

  const binderFlagsForStudy = (study) => {
    const resolved = resolveIsfOpenBinderCategories(userData, study);
    return {
      hasEisf: resolved.hasEisf,
      hasEpsf: resolved.hasEpsf,
    };
  };

  const filteredStudies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return studies;
    return studies.filter((row) => {
      const haystack = [
        row.StudyName,
        row.StudyNumber,
        row.PIName,
        row.StudyStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [studies, search]);

  if (!open) return null;

  const { hasEisf, hasEpsf } = selectedRecord
    ? binderFlagsForStudy(selectedRecord)
    : {
        hasEisf: !!formData?.haseISF,
        hasEpsf: !!formData?.hasePSF,
      };
  const canOpen = hasEisf || hasEpsf;
  const missingEisf = selectedRecord && !hasEisf;
  const missingEpsf = selectedRecord && hasEisf && !hasEpsf;
  const createLabel = missingEisf && !hasEpsf ? "eISF" : "ePSF";
  const canCreate = Boolean(allowCreateBinder);

  return (
    <div className="isf-study-picker-backdrop" role="dialog" aria-modal="true">
      <div className="isf-study-picker">
        <div className="isf-study-picker__header">
          <div>
            <h5 className="isf-study-picker__title">Study binders</h5>
            <p className="isf-study-picker__subtitle">
              {canCreate
                ? "Select a study to open an existing binder or create eISF / ePSF."
                : "Select a shared study to open its eISF or ePSF binder."}
            </p>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        <div className="isf-study-picker__body">
          <aside className="isf-study-picker__list-pane">
            <div className="isf-study-picker__search-wrap">
              <i className="fa fa-search isf-study-picker__search-icon" aria-hidden />
              <input
                type="search"
                className="form-control isf-study-picker__search"
                placeholder="Search studies…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="isf-study-picker__list">
              {studiesLoading && filteredStudies.length === 0 ? (
                <p className="isf-study-picker__empty-list">Loading studies…</p>
              ) : filteredStudies.length === 0 ? (
                <p className="isf-study-picker__empty-list">
                  {search.trim()
                    ? "No studies match your search."
                    : emptyListMessage || "No studies available."}
                </p>
              ) : (
                filteredStudies.map((study) => {
                  const isActive =
                    String(selectedStudy?.value || "").toLowerCase() ===
                    String(study.StudyId).toLowerCase();
                  const color = studyColor(study);
                  const { code, name } = formatStudyListLabel(study);
                  const { hasEisf: rowHasEisf, hasEpsf: rowHasEpsf } =
                    binderFlagsForStudy(study);

                  return (
                    <button
                      key={study.StudyId}
                      type="button"
                      className={`isf-study-picker__item ${isActive ? "is-active" : ""}`}
                      onClick={() => onSelectStudy?.(study)}
                    >
                      <span className="isf-study-picker__item-main">
                        <span className="isf-study-picker__item-head">
                          <span className="isf-study-picker__item-title">
                            <span
                              className="isf-study-picker__study-dot"
                              style={{ backgroundColor: color }}
                              aria-hidden
                            />
                            <span className="isf-study-picker__item-name">
                              {code ? (
                                <span className="isf-study-picker__item-code">[{code}]</span>
                              ) : null}
                              <span className="isf-study-picker__item-study">{name}</span>
                            </span>
                          </span>
                          <StudyLifecycleBadge study={study} />
                        </span>

                        <span className="isf-study-picker__item-meta">
                          <span
                            className={`isf-study-picker__item-pi ${
                              !study.PIName ? "isf-study-picker__item-pi--empty" : ""
                            }`}
                          >
                            <i className="fa fa-user-circle" aria-hidden />
                            {study.PIName || "No PI assigned"}
                          </span>

                          {(rowHasEisf || rowHasEpsf) && (
                            <span className="isf-study-picker__item-binders">
                              <BinderBadge label="eISF" active={rowHasEisf} kind="eisf" />
                              <BinderBadge label="ePSF" active={rowHasEpsf} kind="epsf" />
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="isf-study-picker__detail-pane">
            {!selectedRecord ? (
              <div className="isf-study-picker__placeholder">
                <div className="isf-study-picker__placeholder-icon">
                  <i className="fa fa-layer-group" aria-hidden />
                </div>
                <h6>Select a study</h6>
                <p>Choose a study from the list to view binder status and next steps.</p>
              </div>
            ) : (
              <>
                <div className="isf-study-picker__detail-head">
                  <div>
                    <h6 className="isf-study-picker__detail-title">
                      {selectedRecord.StudyNumber ? (
                        <span
                          className="isf-study-picker__study-code-badge isf-study-picker__study-code-badge--detail"
                          style={{ backgroundColor: studyColor(selectedRecord) }}
                        >
                          {selectedRecord.StudyNumber}
                        </span>
                      ) : null}
                      <span>{selectedRecord.StudyName}</span>
                    </h6>
                    <p>
                      {selectedRecord.PIName ? `PI ${selectedRecord.PIName}` : "No PI assigned"}
                    </p>
                  </div>
                  <div className="isf-study-picker__status-row">
                    <StudyLifecycleBadge study={selectedRecord} />
                    <BinderBadge label="eISF" active={hasEisf} kind="eisf" />
                    <BinderBadge label="ePSF" active={hasEpsf} kind="epsf" />
                  </div>
                </div>

                {canOpen && !showCreatePanel ? (
                  <div className="isf-study-picker__card">
                    <h6 className="isf-study-picker__card-title">Open binder</h6>
                    {canCreate ? (
                      <p className="isf-study-picker__card-text">
                        This study already has{" "}
                        {hasEisf && hasEpsf
                          ? "eISF and ePSF"
                          : hasEisf
                            ? "an eISF"
                            : "an ePSF"}{" "}
                        configured. Choose which binder to open.
                      </p>
                    ) : null}
                    <div className="isf-study-picker__field">
                      <label className="form-label">Binder type</label>
                      {!canCreate &&
                      (templateCategoryOptions || []).length <= 1 ? (
                        <div className="isf-study-picker__binder-plain">
                          {formData?.templateCategory?.label ||
                            templateCategoryOptions?.[0]?.label ||
                            "—"}
                        </div>
                      ) : (
                        <Dropdown
                          options={templateCategoryOptions}
                          selected={formData.templateCategory}
                          onSelect={onSelectTemplateCategory}
                          isMulti={false}
                        />
                      )}
                    </div>
                    <div className="isf-study-picker__actions">
                      {canCreate && (missingEisf || missingEpsf) && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={onRequestCreate}
                        >
                          Create {createLabel}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={onOpenStudy}
                      >
                        Open binder
                      </button>
                    </div>
                  </div>
                ) : null}

                {!canCreate && selectedRecord && !canOpen ? (
                  <div className="isf-study-picker__card">
                    <h6 className="isf-study-picker__card-title">No binder to open</h6>
                    <p className="isf-study-picker__card-text">
                      This study has no shared eISF or ePSF binder for your account.
                      Contact your organisation administrator if you need access.
                    </p>
                  </div>
                ) : null}

                {canCreate &&
                  (!studyHasBinder(selectedRecord) || showCreatePanel) && (
                  <div className="isf-study-picker__card isf-study-picker__card--create">
                    <h6 className="isf-study-picker__card-title">
                      {studyHasBinder(selectedRecord)
                        ? `Create ${createLabel}`
                        : "Create study binder"}
                    </h6>
                    <p className="isf-study-picker__card-text">
                      {studyHasBinder(selectedRecord)
                        ? `Set up ${createLabel} using a template. Multi-site studies can share one template or use different templates per site.`
                        : "Get started by choosing a binder type and template for this study."}
                    </p>

                    <div className="isf-study-picker__field">
                      <label className="form-label">Binder type</label>
                      <Dropdown
                        options={templateCategoryCreationOptions}
                        selected={formData.templateCategoryCreation}
                        onSelect={onSelectTemplateCategoryCreation}
                        isMulti={false}
                      />
                    </div>

                    {formData.templateCategoryCreation ? (
                      <>
                        {templatesLoading ? (
                          <p className="text-muted small mb-2">Loading templates…</p>
                        ) : null}
                        {createModalSitesLoading ? (
                          <p className="text-muted small mb-2">Loading study sites…</p>
                        ) : null}
                        {!createModalSitesLoading &&
                        createModalSites.filter((row) => row.SiteId).length === 1 ? (
                          <p className="text-muted small mb-2">
                            Single site study — the selected template applies to this site.
                          </p>
                        ) : null}
                        {isMultiSiteCreateModal ? (
                          <IsfSiteTemplateFields
                            embedded
                            siteRows={createModalSites}
                            templateOptions={templateOptions}
                            sameTemplateForAll={sameTemplateForAllSites}
                            onSameTemplateForAllChange={onSameTemplateForAllChange}
                            sharedTemplate={formData.selectedTemplateOption}
                            onSharedTemplateChange={onSelectTemplate}
                            perSiteTemplates={perSiteTemplateOptions}
                            onPerSiteTemplateChange={onPerSiteTemplateChange}
                            sharedReferenceDate={createReferenceDate}
                            onSharedReferenceDateChange={onCreateReferenceDateChange}
                            perSiteReferenceDates={perSiteReferenceDates}
                            onPerSiteReferenceDateChange={onPerSiteReferenceDateChange}
                            showSharedTemplate={false}
                            notice={templateSetupInfo}
                          />
                        ) : null}
                        {!isMultiSiteCreateModal || sameTemplateForAllSites ? (
                          <div className="row g-2 isf-study-picker__template-row">
                            <div className="col-md-6">
                              <div className="isf-study-picker__field">
                                <RequiredLabel>Template</RequiredLabel>
                                <Dropdown
                                  options={templateOptions}
                                  selected={formData.selectedTemplateOption}
                                  onSelect={onSelectTemplate}
                                  isMulti={false}
                                  placeholder="Select template"
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="isf-study-picker__field">
                                <RequiredLabel>Reference date</RequiredLabel>
                                <IsfReferenceDatePicker
                                  value={createReferenceDate}
                                  onChange={onCreateReferenceDateChange}
                                />
                                <IsfReferenceDateHint />
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    <div className="isf-study-picker__actions">
                      {showCreatePanel && canOpen ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm px-0"
                          onClick={() => onRequestCreate?.({ cancel: true })}
                        >
                          Back to open binder
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={onCreateStudy}
                      >
                        Create &amp; open
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
